import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as globals from '../../components/GlobalScripts.js';

import '../../styles/cliente/cliente_cupones_disponibles.css';
import { getTodayEc } from './clienteMenuApi.js';

function RefreshIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M21 12a9 9 0 1 1-3-6.7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            <path d="M21 4v6h-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function ChevronIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function formatDiscount(c) {
    const tipo = String(c?.tipo_descuento || '').toLowerCase();
    const valor = Number(c?.valor || 0) || 0;
    const moneda = String(c?.moneda || '').trim();

    if (tipo.includes('porc') || tipo === 'porcentaje' || tipo === 'percent') return `${valor}% OFF`;
    if (moneda) return `${moneda} ${valor}`;
    return `${valor}`;
}

function formatDate10(dt) {
    return String(dt || '').slice(0, 10);
}

/**
 * Módulo cliente: cupones "listos para usar"
 * - asignados (cupones_clientes)
 * - disponibles (públicos)
 *
 * UI: colapsado por defecto (toggle)
 * Render: SOLO si hay cupones y (envios <= 0 || expiró)
 */
export default function ClienteCuponesDisponibles({ renewTo = '/cliente/renovacion' }) {
    const navigate = useNavigate();
    const path_api = localStorage.getItem('path_api');

    const user = useMemo(() => globals.get_user?.() || {}, []);
    const envios = Number(user?.envios || 0) || 0;
    const expDay = String(user?.envios_expira_at || '').slice(0, 10);
    const hoy = getTodayEc ? getTodayEc() : String(new Date().toISOString()).slice(0, 10);
    const isExpired = !!expDay && expDay < hoy;

    const shouldRenderByPlan = envios <= 0 || isExpired;

    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');
    const [asignados, setAsignados] = useState([]);
    const [disponibles, setDisponibles] = useState([]);
    const [openList, setOpenList] = useState(false);

    const allCoupons = useMemo(() => {
        const a = (Array.isArray(asignados) ? asignados : [])
            .filter((x) => Number(x?.is_visible ?? 1) === 1)
            .map((x) => ({ ...x, _scope: 'asignado' }));

        const d = (Array.isArray(disponibles) ? disponibles : [])
            .filter((x) => Number(x?.is_visible ?? 1) === 1)
            .map((x) => ({ ...x, _scope: 'publico' }));

        return [...a, ...d];
    }, [asignados, disponibles]);

    const hasAny = allCoupons.length > 0;

    const fetchMyCoupons = async () => {
        setLoading(true);
        setErr('');

        try {
            const res = await axios.post(`${path_api}/default/cupones/mis_cupones.php`, {
                rute: globals.getRuteBackend?.(),
                session_id: globals.get_session_id?.(),
                keylogin: globals.get_keylogin?.(),
            });

            if (res.data?.mensaje !== 'success') {
                setAsignados([]);
                setDisponibles([]);
                setErr(String(res.data?.mensaje || 'No se pudieron cargar cupones'));
                return;
            }

            setAsignados(Array.isArray(res.data?.asignados) ? res.data.asignados : []);
            setDisponibles(Array.isArray(res.data?.disponibles) ? res.data.disponibles : []);
        } catch (e) {
            setAsignados([]);
            setDisponibles([]);
            setErr(String(e?.message || 'Error al cargar cupones'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!shouldRenderByPlan) return;
        fetchMyCoupons();
    }, [shouldRenderByPlan]);

    if (!shouldRenderByPlan) return null;

    if (!hasAny && !loading) return null;

    const subtitle = loading
        ? 'Cargando…'
        : `Tienes ${allCoupons.length} cupón(es) disponible(s)`;

    return (
        <div className="tw-cpn">
            <div className="tw-cpn__card">
                <div className="tw-cpn__head">
                    <button
                        type="button"
                        className="tw-cpn__toggle"
                        onClick={() => setOpenList((p) => !p)}
                        aria-expanded={openList ? 'true' : 'false'}
                    >
                        <div className="tw-cpn__titlewrap">
                            <div className="tw-cpn__title">Cupones disponibles</div>
                            <div className="tw-cpn__subtitle">
                                {subtitle}
                                {!loading ? <span className="tw-cpn__reason"> • toca para {openList ? 'ocultar' : 'ver'}</span> : null}
                            </div>
                        </div>

                        <div className="tw-cpn__toggleRight">
                            <span className="tw-cpn__offerTag">OFERTA</span>

                            <span className={`tw-cpn__chev ${openList ? 'is-open' : ''}`} aria-hidden="true">
                                <ChevronIcon />
                            </span>
                        </div>
                    </button>

                    <button
                        type="button"
                        className="tw-cpn__iconbtn"
                        onClick={(e) => {
                            e.stopPropagation();
                            fetchMyCoupons();
                        }}
                        disabled={loading}
                        title="Actualizar cupones"
                    >
                        <RefreshIcon />
                    </button>
                </div>

                {err ? <div className="tw-cpn__alert">{err}</div> : null}

                {openList ? (
                    <>
                        <div className="tw-cpn__list">
                            {allCoupons.map((c) => {
                                const code = c?.codigo ? String(c.codigo) : `#${c?.id || ''}`;
                                const title = String(c?.titulo || c?.descripcion || 'Cupón');
                                const exp = c?.expira_at ? formatDate10(c.expira_at) : '';
                                const scopeLabel = c?._scope === 'asignado' ? 'Asignado' : 'Público';

                                return (
                                    <div key={`${c?.id}-${code}-${c?._scope}`} className="tw-cpn__item">
                                        <div className="tw-cpn__top">
                                            <div className="tw-cpn__code">{code}</div>
                                            <div className={`tw-cpn__badge ${c?._scope === 'asignado' ? 'is-assigned' : 'is-public'}`}>
                                                {scopeLabel}
                                            </div>
                                        </div>

                                        <div className="tw-cpn__name">{title}</div>

                                        <div className="tw-cpn__meta">
                                            <span className="tw-cpn__pill">{formatDiscount(c)}</span>
                                            {exp ? (
                                                <span className="tw-cpn__dim">Expira: {exp}</span>
                                            ) : (
                                                <span className="tw-cpn__dim">Sin expiración</span>
                                            )}
                                        </div>

                                        <div className="tw-cpn__actions">
                                            <button
                                                type="button"
                                                className="tw-cpn__btn"
                                                onClick={() => {
                                                    const q = c?.codigo ? `?cupon=${encodeURIComponent(String(c.codigo))}` : '';
                                                    navigate(`${renewTo}${q}`, { state: { coupon: c } });
                                                }}
                                            >
                                                Usar
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="tw-cpn__foot">
                            <div className="tw-cpn__hint">
                                Al dar click en “Usar”, te llevamos a renovación para aplicar el cupón.
                            </div>
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );
}
